/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  ChangeEvent,
  MouseEvent,
  ReactNode,
  memo,
  useState,
} from "react";
import withFilterFormField from "./FilterFormField";
import { Form, Formik, FormikValues } from "formik";
import {
  Box,
  Button,
  Popper,
  Tab,
  Tabs,
  Grid,
  Divider,
  ClickAwayListener,
  Grow,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import * as Yup from "yup";
import FilterBar from "./FilterBar";
import { DynamicField, DynamicFieldProps, Option } from "./DynamicField";
import { useTheme } from "@mui/system";

export enum Entity {
  Terminal = "terminal",
  Merchant = " merchant",
}

export interface FilterFormProps {
  entity: Entity;
  sections: {
    name: string;
    fields: DynamicFieldProps[];
  }[];
  onSubmit?: (values: SubmitFilterParams) => void;
}

export type Query = {
  [key: string]: string | Option[];
};

export interface FilterDataParams {
  page: number;
  pageSize: number;
  pageCount: number;
  orderBy: number;
  orderDirection: string;
  queries: Query;
  totalCount: number;
}

interface SubmitFilterParams {
  entity: Entity;
  values: Query;
}

const DynamicFieldWithFilterForm = withFilterFormField(DynamicField);

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

interface TabPanelProps {
  children?: ReactNode;
  index: unknown;
  value: unknown;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      width={420}
      style={{ overflow: "hidden", overflowY: "auto" }}
      {...other}
    >
      {value === index && <Box p={2}>{children}</Box>}
    </Box>
  );
}

const FilterForm = ({ entity, sections, onSubmit }: FilterFormProps) => {
  const theme = useTheme();
  const classes = {
    root: {
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      display: "flex",
      height: 300,
    },
    tabs: {
      borderRight: `1px solid ${theme.palette.divider}`,
      "& .MuiTabs-indicator": {
        backgroundColor: "var(--dmg-blue-secondary)",
      },
      "& .MuiTab-root": {
        minHeight: 30,
      },
      "& .MuiTab-wrapper": {
        alignItems: "start",
        fontSize: 14,
        textAlign: "left",
        maxWidth: 150,
      },
      "& .Mui-selected": {
        fontWeight: 600,
        background: "rgba(173, 173, 173, 0.09)",
      },
      "& .MuiTabScrollButton-vertical": {
        background: "rgba(173, 173, 173, 0.2)",
        height: "unset",
        minHeight: 25,
        transition: "opacity 0.3s ease-in-out, min-height 0.2s ease-in-out",
      },
      "& .MuiTabScrollButton-vertical.Mui-disabled": {
        minHeight: 0,
        opacity: 0,
      },
    },
    tab: {
      textTransform: "capitalize",
      minWidth: 150,
      alignItems: "start",
      color: "var(--dmg-text-primary)",
    },
    tabPanel: {
      width: "200px",
    },
    filterWrapper: {
      zIndex: 10,
      background: "var(--dmg-bg)",
    },
    boxBorder: {
      border: "1px solid rgba(0, 0, 0, 0.12)",
      borderBottom: "none",
      borderRadius: 2,
      marginTop: 0.5,
    },
    filterBottomSection: {
      borderTop: `1px solid ${theme.palette.divider}`,
      display: "flex",
      justifyContent: "space-between",
      padding: 1,
      background: "var(--dmg-gray-100)",
    },
    divider: {
      marginBottom: 2,
    },
    filter: {
      display: "flex",
      alignItems: "center",
      width: "100%",
    },
    filterBar: {
      [theme.breakpoints.down("md")]: {
        display: "none",
      },
    },
    hide: {
      display: "none",
    },
  };
  const [isShowFilter, setIsShowFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const handleOpenFilter = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setIsShowFilter(!isShowFilter);
  };

  const handleChangeFilterTab = (
    event: ChangeEvent<object>,
    newValue: number
  ) => {
    setTabValue(newValue);
  };

  const handleClickAway = () => {
    setIsShowFilter(false);
  };

  const formatFilterData = (values: FormikValues) => {
    const result: FormikValues = {};

    for (const key in values) {
      if (typeof values[key] === "object") {
        if (Array.isArray(values[key])) {
          const valuesArray: string[] = [];
          for (const item of values[key]) {
            if (item.value !== undefined) {
              valuesArray.push(item.value);
            }
          }

          if (valuesArray.length) {
            result[key] = valuesArray.join(",");
          }
        } else {
          if (values[key].value !== undefined && values[key].value !== "") {
            result[key] = values[key].value;
          }
        }
      } else if (values[key] !== "") {
        result[key] = values[key];
      }
    }

    return result;
  };

  const handleSubmitFilterForm = (values: FormikValues) => {
    setIsShowFilter(false);
    const filteredQueries = formatFilterData(values);

    onSubmit && onSubmit({ entity, values: filteredQueries });
  };

  const initialValues = sections.reduce((acc, section) => {
    section.fields.forEach((field) => {
      if (field.fieldType === "select") {
        // @ts-ignore
        acc[field.name] = { label: "", value: "" };
        // acc[field.name] =
        //   field.options && field.options.length ? field.options[0].value : "";
      } else if (
        field.fieldType === "checkbox" ||
        field.fieldType === "switch" ||
        field.fieldType === "autocomplete"
      ) {
        // @ts-ignore
        acc[field.name] = [];
      } else {
        // @ts-ignore
        acc[field.name] = "";
      }
    });
    return acc;
  }, {});

  const validationSchema = Yup.object().shape(
    sections.reduce((schema, section) => {
      section.fields.forEach((field) => {
        if (field.validationSchema) {
          // @ts-ignore
          schema[field.name] = field.validationSchema;
        }
      });
      return schema;
    }, {})
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmitFilterForm(values)}
    >
      {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
        // handle remove item from Filter bar
        const handleRemoveFilterBar = (fieldName: string) => {
          setFieldValue(`['${fieldName}']`, Array.isArray(fieldName) ? [] : "");

          const newFilterObj = {
            ...values,
          } as FormikValues;
          delete newFilterObj[fieldName];

          handleSubmitFilterForm(newFilterObj as FormikValues);
        };

        return (
          <>
            <Box sx={classes.filter}>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                endIcon={<TuneIcon />}
                onClick={handleOpenFilter}
                style={{
                  border: "1px solid var(--dmg-purple)",
                  backgroundColor: "white",
                  color: "var(--dmg-primary)",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
              >
                Filter
              </Button>

              <Box marginLeft={1} sx={classes.filterBar}>
                <FilterBar
                  sections={sections}
                  isSubmitted={isSubmitting}
                  formatFilterData={formatFilterData}
                  handleRemoveFilter={handleRemoveFilterBar}
                />
              </Box>
            </Box>

            <Popper
              open={isShowFilter}
              anchorEl={anchorEl}
              placement="bottom-start"
              transition
              sx={classes.filterWrapper}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Box boxShadow={1} sx={classes.boxBorder}>
                    <ClickAwayListener onClickAway={handleClickAway}>
                      <Form onSubmit={handleSubmit}>
                        <Box sx={classes.root}>
                          <Tabs
                            orientation="vertical"
                            variant={
                              sections.length > 6 ? "scrollable" : "standard"
                            }
                            value={tabValue}
                            onChange={handleChangeFilterTab}
                            sx={classes.tabs}
                          >
                            {sections.map((section, idx) => (
                              <Tab
                                key={idx}
                                label={section.name}
                                {...a11yProps(idx)}
                                sx={classes.tab}
                              />
                            ))}
                          </Tabs>

                          {sections.map((section, idx) => (
                            <TabPanel key={idx} value={tabValue} index={idx}>
                              <Grid container spacing={2}>
                                {section.fields
                                  .filter(field=> field.disabled !== true)
                                  .map((field) => (
                                  <Grid
                                    key={field.name}
                                    item
                                    xs={12}
                                    md={field.fullWidth ? 12 : 6}
                                    style={
                                      field.options &&
                                      field.options.length === 0
                                        ? classes.hide
                                        : null
                                    }
                                  >
                                    {field.fullWidth && (
                                      <Divider light sx={classes.divider} />
                                    )}
                                    <DynamicFieldWithFilterForm
                                      fieldType={field.fieldType}
                                      label={field.label}
                                      name={field.name}
                                      options={field.options}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </TabPanel>
                          ))}
                        </Box>

                        <Box sx={classes.filterBottomSection}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            type="reset"
                          >
                            Clear Filters
                          </Button>

                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Search
                          </Button>
                        </Box>
                      </Form>
                    </ClickAwayListener>
                  </Box>
                </Grow>
              )}
            </Popper>
          </>
        );
      }}
    </Formik>
  );
};

export default memo(FilterForm);
